import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CabecalhoPadrao from "../components/CabecalhoPadrao/CabecalhoPadrao";
import DropdownPadrao from "../components/DropdownPadrao/DropdownPadrao";
import FiltroPadrao from "../components/FiltroPadrao/FiltroPadrao";
import InputPadrao from "../components/InputPadrao/InputPadrao";
import TabelaPadrao from "../components/TabelaPadrao/TabelaPadrao";
import FiltroURLWrapper from "../hooks/FiltroUrlHook";
import api from '../services/api';
import FiltroUtils from '../utils/FiltroUtils';
import { APP_ROUTES, getEditarPath } from "../services/app_routes";
import { PERMISSOES } from "../utils/PermissaoUtils";
import PAGINATION from "../utils/PaginacaoUtils";
import Carregamento from "../components/Carregamento/Carregamento";
import { BREAKPOINTS_PADRAO, OPCOES_SITUACAO, COLUNA_SITUACAO, definirColunasFixas, criarColuna } from "../utils/ListagemUtils";
import Mensagens from "../utils/MensagemUtils";
import NotificacaoUtils from "../utils/NotificacaoUtils";
import { TipoFormatacao } from "../utils/FormatacaoUtils";

const ListagemEmpresa = () => {
    const navigate = useNavigate();

    const [empresas, setEmpresas] = useState([]);
    const [totalEmpresas, setTotalEmpresas] = useState(0);
    const [carregando, setCarregando] = useState(false);
    const [buscaInicial, setBuscaInicial] = useState(true);
    
    const filtrarRef = useRef(null);
    const currentPageRef = useRef(PAGINATION.DEFAULT_FIRST_PAGE);

    const filtrosPadrao = {
      razaoSocial: '',
      documento: '',
      situacaoCadastro: '',
    };

    const colunas = [
      { titulo: Mensagens.EMPRESA.LISTAGEM.COLUNA_RAZAO_SOCIAL, campo: 'razaoSocial' },
      COLUNA_SITUACAO(),
      criarColuna(Mensagens.CLIENTE.LISTAGEM.COLUNA_DOCUMENTO, 'documento'),
    ];
    
    const colunasFixas = definirColunasFixas();
    useEffect(() => {
        // Garantir que a busca inicial seja feita com situacaoCadastro=ATIVO
        if (filtrarRef.current) filtrarRef.current(0); // Forçar página 0 para busca inicial
    }, []); 

    return (
      <FiltroURLWrapper
        defaultFilters={filtrosPadrao}
        defaultPage={PAGINATION.DEFAULT_FIRST_PAGE}
        defaultSize={PAGINATION.DEFAULT_PAGE_SIZE}
        basePath={'/empresa/visualizar'}
      >
        {({
          currentPage,
          pageSize,
          filtros,
          setFiltros,
          aplicarFiltros,
          limparFiltros,
          proximaPagina,
          paginaAnterior,
          irParaPagina,
        }) => {
          const fetchEmpresas = async queryString => {
            setCarregando(true);
            try {
              const apiResponse = await api.get('/empresa_' + queryString);
              if (apiResponse.success) {
                const data = apiResponse.data;
                const empresas = data.content;
                const page = data.page;
                setEmpresas(empresas);
                setTotalEmpresas(page.totalElements);
              } else {
                NotificacaoUtils.erro(Mensagens.ERRO.ERRO_BUSCAR_DADOS);
                setEmpresas([]);
                setTotalEmpresas(0);
              }
            } catch (error) {
              console.error('Erro ao buscar empresas:', error);
              NotificacaoUtils.erro(Mensagens.ERRO.ERRO_BUSCAR_DADOS);
              setEmpresas([]);
              setTotalEmpresas(0);
            } finally {
              setCarregando(false);
            }
          };

          // filtrar agora aceita page como argumento
          const filtrar = async (page = 0) => {
            // Se for a busca inicial (primeira execução) e não há filtro de situação definido, aplicar ATIVO
            if (buscaInicial && !filtros.situacaoCadastro) {
              const filtrosComSituacao = { ...filtros, situacaoCadastro: "ATIVO" };
              // Atualiza o estado dos filtros para que o dropdown mostre "ATIVO"
              setFiltros(filtrosComSituacao);
              setBuscaInicial(false);
              
              const queryStringComSituacao = FiltroUtils.applyPageFilter(page, pageSize, filtrosComSituacao);
              await fetchEmpresas(queryStringComSituacao);
            } else {
              // Para buscas subsequentes, usar os filtros exatamente como o usuário definiu
              const { queryString } = aplicarFiltros(page);
              await fetchEmpresas(queryString);
            }
          };
          
          if (filtrarRef.current !== filtrar) {
            filtrarRef.current = filtrar;
          }
          
          if (currentPageRef.current !== currentPage) {
            currentPageRef.current = currentPage;
            filtrar(currentPage);
          }

          // Sempre que pesquisar, volta para página 0 e filtra
          const pesquisar = async () => {
            setBuscaInicial(false); // Marcar que não é mais busca inicial
            irParaPagina(0);
            await filtrar(0);
          };

          // Função customizada para limpar filtros
          const limparFiltrosCustom = () => {
            setBuscaInicial(false); // Marcar que não é mais busca inicial
            limparFiltros();
          };

          const handleEditar = empresa => {
            if (empresa.id) {
              navigate(getEditarPath(APP_ROUTES.EMPRESA_EDITAR, empresa.id));
            }
          };

          const handleCriarEmpresa = () => {
            navigate(APP_ROUTES.EMPRESA_CADASTRO);
          };

          return (
            <div>
              {carregando && <Carregamento />}

              <CabecalhoPadrao
                titulo={Mensagens.EMPRESA.LISTAGEM.TITULO}
                permissao={PERMISSOES.EMPRESA_CADASTRAR}
                onClick={handleCriarEmpresa}
              />

              <FiltroPadrao filtrar={pesquisar} limparFiltros={limparFiltrosCustom}>
                <InputPadrao
                  label={Mensagens.EMPRESA.LISTAGEM.COLUNA_RAZAO_SOCIAL}
                  placeholder={Mensagens.EMPRESA.LISTAGEM.COLUNA_RAZAO_SOCIAL_PLACEHOLDER}
                  value={filtros.razaoSocial}
                  name="razaoSocial"
                  onChange={e => setFiltros({ ...filtros, razaoSocial: e.target.value })}
                />
                <InputPadrao
                  label={Mensagens.EMPRESA.LISTAGEM.FILTRO_DOCUMENTO}
                  placeholder={Mensagens.EMPRESA.LISTAGEM.FILTRO_DOCUMENTO_PLACEHOLDER}
                  value={filtros.documento}
                  tipoFormatador={TipoFormatacao.DOCUMENTO}
                  type={InputPadrao.TipoInput.DOCUMENTO}
                  name="documento"
                  onChange={e => setFiltros({ ...filtros, documento: e.target.value })}
                />
                <DropdownPadrao
                  label={Mensagens.EMPRESA.LISTAGEM.FILTRO_SITUACAO}
                  showFilter={true}
                  options={OPCOES_SITUACAO}
                  value={filtros.situacaoCadastro}
                  name="situacaoCadastro"
                  onChange={e => setFiltros({ ...filtros, situacaoCadastro: e.target.value })}
                />
              </FiltroPadrao>

              <TabelaPadrao
                permissaoEditar={PERMISSOES.EMPRESA_EDITAR}
                dados={empresas}
                colunas={colunas}
                totalItems={totalEmpresas}
                paginaAtual={currentPage}
                tamanhoPagina={pageSize}
                carregando={carregando}
                onEditar={handleEditar}
                onProximaPagina={proximaPagina}
                onPaginaAnterior={paginaAnterior}
                onIrParaPagina={irParaPagina}
                colunasFixas={colunasFixas}
                breakpoints={BREAKPOINTS_PADRAO}
              />
            </div>
          );
        }}
      </FiltroURLWrapper>
    );
};
 
export default ListagemEmpresa;